import { useContext, useState } from 'react';
import { Autocomplete, Box, Button, Modal, Stack, TextField, Typography } from '@mui/material';
import { modalStyle } from '@/components/modalStyle';
import { enqueueSnackbar } from 'notistack';
import { uploadFile } from '../../utils/api-helpers';
import { ImageCropComponent } from '@/components/images/ImageCropComponent';
import { FileInputButton } from '@/components/FileInput';
import { Photo } from '@mui/icons-material';
import { objectHasId } from '@/utils/create-mode-helper';
import { useAdminAllUsers } from '@/hooks/use-all-users.hook';
import { ImageAdder } from '@/components/ImageAdder';
import { CreateDeckDTO, DeckDTO } from '@/generated';
import { useCreateDeck, useUpdateDeck } from '@/hooks/deck-mutation.hooks';
import { AppContext } from '@/contexts/app.context';
import { isTrainerUser } from '@/utils/user';
import { TraineesAutocomplete } from '@/components/TraineesAutocomplete';

export interface UpdateDeckModalProps {
  show: boolean;
  deck: DeckDTO | CreateDeckDTO;
  setShowCreateDeckModal: (s: boolean) => void;
}

export const UpdateDeckModal = (props: UpdateDeckModalProps) => {
  //TODO: UseReducer?
  //TODO: Merge to one set object/state
  const { deck, show: showEditDeckModal, setShowCreateDeckModal } = props;
  const { state } = useContext(AppContext);

  const [deckDescription, setDeckDescription] = useState<string | undefined>(deck.description);
  const [editorIds, setEdtiorIds] = useState<Array<string>>(props.deck.editorIds ?? []);
  const [deckName, setDeckName] = useState<string>(deck.name);
  const [imageFile, setImageFile] = useState<File | null | undefined>();
  const [cardBackImageFiles, setCardBackImageFiles] = useState<Array<File>>([]);
  const [cardBackImageIds, setCardBackImageIds] = useState<Array<string>>(props.deck.cardBackImageIds ?? []);
  const [deckOwnerId, setDeckOwnerId] = useState<string | undefined>(props.deck.ownerId);
  const isEditMode = objectHasId(deck);
  const { userList } = useAdminAllUsers();
  const createDeckMutation = useCreateDeck();
  const updateDeckMutation = useUpdateDeck();

  return (
    <Modal open={showEditDeckModal} onClose={() => setShowCreateDeckModal(false)}>
      <Box sx={modalStyle}>
        <Stack>
          <Typography variant="h5">{isEditMode ? 'Edit Deck' : 'Create Deck'}</Typography>
          <TextField
            value={deckName}
            sx={{ my: 2 }}
            onChange={(e: any) => {
              setDeckName(e.target.value);
            }}
            label="Name"
          />
          <Box sx={{ m: 1 }}>
            <ImageCropComponent imageFile={imageFile} onImageFileChanged={setImageFile} imageId={props.deck.imageId} />
          </Box>
          <FileInputButton sx={{ mt: 1 }} accept=".png,.jpg,.jpeg" startIcon={<Photo />} onFilePicked={(f) => setImageFile(f)}>
            Change Image
          </FileInputButton>
          <TextField value={deckDescription} label={'Deck Description'} onChange={(v: any) => setDeckDescription(v.target.value)} style={{ marginTop: 10 }} multiline />
          {userList !== undefined ? (
            <Autocomplete
              sx={{ mt: 1 }}
              onChange={(e, users) => {
                const ids = users.map((u) => u?.userId).filter((ux) => ux !== undefined) as string[];
                if (ids) setEdtiorIds(ids);
              }}
              defaultValue={deck.editorIds !== undefined ? deck.editorIds?.map((id) => userList.find((u) => u.userId === id)) : []}
              getOptionLabel={(option) => option?.username ?? option?.email ?? 'unknown'}
              renderInput={(params) => <TextField variant="outlined" {...params} label={`Editors`} placeholder={`Editors`} />}
              options={userList}
              multiple
            ></Autocomplete>
          ) : (
            <></>
          )}

          <Box>
            <Typography sx={{ my: 2 }}>Card Back Images</Typography>
            <ImageAdder
              imageFileIds={cardBackImageIds}
              filesUpdated={(files) => {
                setCardBackImageFiles(files);
              }}
              fileIdsUpdated={(ids) => {
                setCardBackImageIds(ids);
              }}
            />
          </Box>
          {isTrainerUser(state?.userProfile) ? (
            <Box>
              Deck Owner:
              <TraineesAutocomplete setSelectedTraineeId={setDeckOwnerId} selectedTraineeId={deckOwnerId} />
            </Box>
          ) : (
            <></>
          )}

          <Box>
            <Button
              style={{ marginTop: 10 }}
              onClick={async () => {
                setShowCreateDeckModal(false);
                try {
                  let fileId = deck.imageId;
                  if (imageFile) {
                    fileId = await uploadFile(imageFile);
                  }

                  let backgroundIds = cardBackImageIds;

                  if (cardBackImageFiles.length) {
                    let fileIds = await Promise.all(cardBackImageFiles.map((f) => uploadFile(f)));
                    backgroundIds = [...backgroundIds, ...fileIds];
                  }
                  if (objectHasId(deck)) {
                    await updateDeckMutation.mutateAsync({
                      ...deck,
                      name: deckName || '',
                      description: deckDescription,
                      imageId: fileId,
                      editorIds,
                      ownerId: deckOwnerId,
                      cardBackImageIds: backgroundIds,
                    });
                  } else {
                    await createDeckMutation.mutateAsync({
                      name: deckName,
                      description: deckDescription,
                      imageId: fileId,
                      editorIds,
                      cardBackImageIds: backgroundIds,
                      ownerId: deckOwnerId,
                    });
                  }
                } catch (e) {
                  if (e instanceof Error) {
                    enqueueSnackbar(JSON.parse(e.message).message, { variant: 'error' });
                  }
                }
              }}
            >
              Save
            </Button>
            <Button
              style={{ marginTop: 10 }}
              onClick={() => {
                setShowCreateDeckModal(false);
                setCardBackImageIds(props.deck.cardBackImageIds ?? []);
                setDeckDescription(deck.description);
                setEdtiorIds(deck.editorIds ?? []);
                setDeckName(deck.name);
              }}
            >
              Close
            </Button>
          </Box>
        </Stack>
      </Box>
    </Modal>
  );
};
